import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import momentPropTypes from 'react-moment-proptypes'
import { useHistory, useLocation } from 'react-router-dom'
import queryString from 'query-string'
import moment from 'moment-timezone'

import Calendar from '../leevo_ui/Calendar/Calendar'
import EventDrawer from '../leevo_ui/EventDrawer/EventDrawer'
import DayEventsDrawer from '../leevo_ui/DayEventsDrawer/DayEventsDrawer'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import useFacility from '../../utilities/hooks/useFacility'

function coachClassPath(facilityId, klassId, { klassDate, dayKey, timeKey }, timeZone) {
  if (dayKey && timeKey) {
    return `/facilities/${facilityId}/classes/${klassId}/class_days/${dayKey}/${timeKey}`
  }
  const m = moment(klassDate).tz(timeZone)
  return `/facilities/${facilityId}/classes/${klassId}/class_days/${m.format('YYYY-MM-DD')}/${m.format('HH:mm')}`
}

function legacyCoachClassPath(facilityId, klassId, dateParam, timeParam, timeZone) {
  const dayStr = moment(dateParam).tz(timeZone).format('YYYY-MM-DD')
  return `/facilities/${facilityId}/classes/${klassId}/class_days/${dayStr}/${timeParam}`
}

function FacilitiesKlassDaysIndex({ facility, klasses = [], selectedMonth, isLoadingKlasses = false, onMonthChange }) {
  const { isLoading: isLoadingFacility } = useFacility()
  const history = useHistory()
  const location = useLocation()
  const [previewEvent, setPreviewEvent] = useState(null)
  const [dayEventsDrawerOpen, setDayEventsDrawerOpen] = useState(false)
  const [selectedDay, setSelectedDay] = useState({
    date: null,
    klasses: []
  })

  const buildSearchParams = useCallback(
    (newParams) => {
      const currentParams = queryString.parse(location.search)
      const monthParam = currentParams.month ? { month: currentParams.month } : {}
      return queryString.stringify({ ...monthParam, ...newParams })
    },
    [location.search]
  )

  useEffect(() => {
    const params = queryString.parse(location.search)

    if (params.klass && params.date && params.time && facility?.id) {
      const coachUrl = legacyCoachClassPath(facility.id, params.klass, params.date, params.time, facility.timeZone)
      history.replace(coachUrl)
      return
    }

    if (params.day) {
      const klassesForDay = klasses.filter((k) => {
        const klassDate = moment(k.date).format('YYYY-MM-DD')
        return klassDate === params.day
      })
      if (klassesForDay.length > 0) {
        setSelectedDay({ date: params.day, klasses: klassesForDay })
        setDayEventsDrawerOpen(true)
      }
    } else {
      setDayEventsDrawerOpen(false)
      setSelectedDay({ date: null, klasses: [] })
      setPreviewEvent(null)
    }
  }, [location.search, klasses, facility?.id, facility?.timeZone, history])

  function handleEventClick(klassId, date, time, klassesForDay) {
    if (klassId) {
      const dayKey = moment(date).tz(facility.timeZone).format('YYYY-MM-DD')
      history.replace({ pathname: location.pathname, search: buildSearchParams({ day: dayKey }) })
      history.push(coachClassPath(facility.id, klassId, { klassDate: date }, facility.timeZone))
    } else if (klassesForDay && klassesForDay.length > 0) {
      setSelectedDay({ date, klasses: klassesForDay })
      setDayEventsDrawerOpen(true)
      history.push({ search: buildSearchParams({ day: moment(date).tz(facility.timeZone).format('YYYY-MM-DD') }) })
    }
  }

  function handlePreviewClose() {
    setPreviewEvent(null)
  }

  function handleViewFullClassFromPreview() {
    if (!previewEvent) return
    history.replace({ pathname: location.pathname, search: buildSearchParams({ day: previewEvent.dayKey }) })
    history.push(
      coachClassPath(
        facility.id,
        previewEvent.klassId,
        { dayKey: previewEvent.dayKey, timeKey: previewEvent.timeHHmm, klassDate: previewEvent.klassDate },
        facility.timeZone
      )
    )
    setPreviewEvent(null)
  }

  function handleDayEventOpenClass(klassId, dayKey, timeHHmm, klassDate = null) {
    history.replace({ pathname: location.pathname, search: buildSearchParams({ day: dayKey }) })
    history.push(coachClassPath(facility.id, klassId, { dayKey, timeKey: timeHHmm, klassDate }, facility.timeZone))
  }

  function handleDayEventPreview(klassId, dayKey, timeHHmm, klassDate = null) {
    setPreviewEvent({ klassId, klassDate, timeHHmm, dayKey })
  }

  function handleDayEventsDrawerClose() {
    setDayEventsDrawerOpen(false)
    setPreviewEvent(null)
    history.push({ search: buildSearchParams({}) })
    setTimeout(() => {
      setSelectedDay({ date: null, klasses: [] })
    }, 300)
  }

  return (
    <ApplicationLayout isContentLoading={isLoadingFacility || isLoadingKlasses}>
      <div className="flex-1 p-4 overflow-auto lg:p-8">
        <div className="mx-auto max-w-7xl">
          <Calendar
            className="w-full"
            klasses={klasses}
            timeZone={facility.timeZone}
            isLoading={isLoadingKlasses}
            providedMonth={selectedMonth}
            onMonthChange={onMonthChange}
            onEventClick={handleEventClick}
          />
        </div>
      </div>

      <DayEventsDrawer
        open={dayEventsDrawerOpen}
        onClose={handleDayEventsDrawerClose}
        date={selectedDay.date}
        timeZone={facility.timeZone}
        klasses={selectedDay.klasses}
        onEventOpenClass={handleDayEventOpenClass}
        onEventPreview={handleDayEventPreview}
      />

      <EventDrawer
        open={!!previewEvent}
        onClose={handlePreviewClose}
        klassId={previewEvent?.klassId}
        date={previewEvent?.klassDate}
        time={previewEvent?.timeHHmm}
        facilityId={facility.id}
        timeZone={facility.timeZone}
        onViewFullClass={handleViewFullClassFromPreview}
      />
    </ApplicationLayout>
  )
}

FacilitiesKlassDaysIndex.propTypes = {
  facility: PropTypes.object,
  klasses: PropTypes.array,
  selectedMonth: momentPropTypes.momentObj,
  isLoadingKlasses: PropTypes.bool,
  onMonthChange: PropTypes.func
}

export default FacilitiesKlassDaysIndex
